// External
import React, { useState, useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import * as _ from 'lodash';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Internal
import Dropzone from '../../../../components/Dropzone';
import {
  ListingDetails_ListingData_listing,
  ListingDetails_ListingData_listing_property_media,
} from '../../../../types/graph/ListingDetails_ListingData';
import SortableDocument from './Documents/SortableDocument';
import Document from './Documents/Document';

// Types & Interfaces
interface IDocumentProps {
  url: string;
  onDeleteDocument: (arg: string) => void;
  [x: string]: any;
}
interface IProps {
  listing: ListingDetails_ListingData_listing;
}

// Component
const Documents: React.FC<IProps> = ({ listing }) => {
  const [items, setItems] = useState<
    ListingDetails_ListingData_listing_property_media[]
  >(_.get(listing, 'property.media', []));

  const [saving, setSaving] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const [updateListing, { loading, error, data }] = useMutation(
    LISTING_UPDATE,
    {
      onCompleted: (data) => {
        setSaving(false);
      },
    }
  );

  // Update items on listing change
  useEffect(() => {
    setItems(_.get(listing, 'property.media', []));
  }, [listing]);

  const onDrop = (files: File[]) => {
    setSaving(true);
    updateListing({
      variables: {
        listingId: listing.id,
        listing: { property: { mediaUpdate: { upload: files } } },
      },
    });
  };

  const onDeleteDocument = (delSid: string) => {
    updateListing({
      variables: {
        listingId: listing.id,
        listing: {
          property: {
            mediaUpdate: {
              remove: delSid,
            },
          },
        },
      },
    });
  };

  const onReorder = (
    files: ListingDetails_ListingData_listing_property_media[]
  ) => {
    updateListing({
      variables: {
        listingId: listing.id,
        listing: {
          property: {
            mediaUpdate: {
              order: files.map((i) => {
                return { sid: i.sid };
              }),
            },
          },
        },
      },
    });
  };

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    let orderedItems: ListingDetails_ListingData_listing_property_media[] = [];

    if (active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.sid === active.id);
        const newIndex = items.findIndex((i) => i.sid === over?.id);

        orderedItems = arrayMove(items, oldIndex, newIndex);

        return orderedItems;
      });
    }

    onReorder(orderedItems);

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  // User feedback on load/save
  if (saving) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div>
        <Dropzone
          label="Upload documents"
          comment="PDF, DOC or compatible document types"
          onDrop={onDrop}
        />
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={items.filter((i) => i.type === 'document').map((i) => i.sid!)}
          strategy={verticalListSortingStrategy}
        >
          <div className="mt-8 flex flex-col  gap-4">
            {items
              .filter((i) => i.type === 'document')
              .map((item, index) => (
                <SortableDocument
                  key={item.sid!}
                  id={item.sid!}
                  item={item}
                  index={index}
                  active={activeId}
                  onDeleteDocument={onDeleteDocument}
                />
              ))}
          </div>
        </SortableContext>

        <div className="flow-root mt-6"></div>

        <DragOverlay adjustScale={true}>
          {activeId ? (
            <Document
              id={items[items.findIndex((i) => i.sid === activeId)].sid}
              key={items[items.findIndex((i) => i.sid === activeId)].sid}
              item={items[items.findIndex((i) => i.sid === activeId)]}
              index={items.findIndex((i) => i.sid === activeId)}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default Documents;

const LISTING_UPDATE = gql`
  mutation Listing_DocumentsUpdateData(
    $listingId: String!
    $listing: ListingInput!
  ) {
    listingUpdate(listingId: $listingId, listing: $listing) {
      id
      sid
      property {
        media {
          sid
          type
          filePath
          fileName
          fileType
          fileExtension
          originalFileName
          embedId
          embedProvider
          embedType
          resourceType
        }
      }
    }
  }
`;
